import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TopBar } from './TopBar';
import { DesktopIcon } from './DesktopIcon';
import { Window } from './Window';
import { Terminal } from './Terminal';
import { CA_CONTENT, ABOUT_CONTENT } from '@/content/tokenContent';

// Window content components
const TextFileViewer = ({ content }) => (
  <div className="h-full p-4 overflow-auto font-mono text-xs text-primary/90 whitespace-pre-wrap leading-relaxed">
    {content}
  </div>
);

const TuxRacerGame = () => (
  <div className="h-full w-full flex flex-col bg-black">
    <iframe
      src="/tuxracer/index.html"
      title="TUX Racer"
      className="flex-1 w-full border-0"
      allow="autoplay; fullscreen"
    />
  </div>
);

// SuperTux Game Component - Self-hosted WebAssembly game
const SuperTuxGame = () => {
  return (
    <div className="h-full w-full flex flex-col bg-black">
      <iframe
        src="/supertux/index.html"
        title="SuperTux"
        className="flex-1 w-full border-0"
        allow="autoplay; fullscreen; cross-origin-isolated"
      />
    </div>
  );
};

// Desktop icons configuration
const DESKTOP_ICONS = [
  { id: 'ca', name: 'CA.txt', type: 'txt' },
  { id: 'about', name: 'ABOUT.md', type: 'md' },
  { id: 'tuxracer', name: 'TUX-RACER', type: 'game' },
  { id: 'supertux', name: 'SUPERTUX', type: 'game' },
];

// Window configurations
const WINDOW_CONFIGS = {
  ca: {
    title: 'CA.txt - Text Editor',
    width: 650,
    height: 500,
  },
  about: {
    title: 'ABOUT.md - Markdown Viewer',
    width: 650,
    height: 520,
  },
  tuxracer: {
    title: 'TUX Racer',
    width: 850,
    height: 600,
  },
  supertux: {
    title: 'SuperTux',
    width: 900,
    height: 650,
  },
  terminal: {
    title: 'tux@tuxos: ~',
    width: 720,
    height: 360,
  },
};

export const Desktop = () => {
  const [windows, setWindows] = useState([]);
  const [maxZIndex, setMaxZIndex] = useState(10);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const desktopRef = useRef(null);
  const windowCountRef = useRef(0);

  // Open terminal on mount
  useEffect(() => {
    openWindow('terminal', { x: 20, y: window.innerHeight - 420 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getWindowPosition = useCallback((windowId) => {
    windowCountRef.current += 1;
    const offset = (windowCountRef.current % 5) * 30;
    
    const basePositions = {
      ca: { x: 150 + offset, y: 80 + offset },
      about: { x: 200 + offset, y: 100 + offset },
      tuxracer: { x: 100 + offset, y: 40 + offset },
      terminal: { x: 20, y: window.innerHeight - 420 },
    };
    
    return basePositions[windowId] || { x: 100 + offset, y: 100 + offset };
  }, []);

  const openWindow = useCallback((windowId, customPosition = null) => {
    setWindows(prev => {
      // Check if window already exists
      const existing = prev.find(w => w.id === windowId);
      if (existing) {
        // Bring to front
        const newZIndex = maxZIndex + 1;
        setMaxZIndex(newZIndex);
        return prev.map(w => 
          w.id === windowId ? { ...w, zIndex: newZIndex } : w
        );
      }
      
      // Create new window
      const newZIndex = maxZIndex + 1;
      setMaxZIndex(newZIndex);
      const position = customPosition || getWindowPosition(windowId);
      
      return [...prev, { 
        id: windowId, 
        zIndex: newZIndex,
        position,
        instanceId: `${windowId}-${Date.now()}`
      }];
    });
  }, [maxZIndex, getWindowPosition]);

  const closeWindow = useCallback((windowId) => {
    setWindows(prev => prev.filter(w => w.id !== windowId));
  }, []);

  const focusWindow = useCallback((windowId) => {
    setWindows(prev => {
      const newZIndex = maxZIndex + 1;
      setMaxZIndex(newZIndex);
      return prev.map(w => 
        w.id === windowId ? { ...w, zIndex: newZIndex } : w
      );
    });
  }, [maxZIndex]);

  const handleIconDoubleClick = (iconId) => {
    openWindow(iconId);
  };

  const handleDesktopClick = (e) => {
    // Deselect icon when clicking on desktop background
    if (e.target === desktopRef.current || e.target.classList.contains('desktop-area')) {
      setSelectedIcon(null);
    }
  };

  const renderWindowContent = (windowId) => {
    switch (windowId) {
      case 'terminal':
        return <Terminal onOpenWindow={openWindow} />;
      case 'ca':
        return <TextFileViewer content={CA_CONTENT} />;
      case 'about':
        return <TextFileViewer content={ABOUT_CONTENT} />;
      case 'tuxracer':
        return <TuxRacerGame />;
      case 'supertux':
        return <SuperTuxGame />;
      default:
        return null;
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-background">
      {/* Top Bar */}
      <TopBar />

      {/* Desktop Area */}
      <div 
        ref={desktopRef}
        className="flex-1 relative desktop-area"
        onClick={handleDesktopClick}
        style={{
          background: `url('/wallpaper.png') center center / cover no-repeat`,
          backgroundColor: 'hsl(0 0% 3%)',
        }}
      >
        {/* Subtle dark overlay for better icon visibility */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.3) 100%)',
          }}
        />

        {/* Desktop Icons */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {DESKTOP_ICONS.map((icon) => (
            <DesktopIcon
              key={icon.id}
              name={icon.name}
              type={icon.type}
              isSelected={selectedIcon === icon.id}
              onDoubleClick={() => handleIconDoubleClick(icon.id)}
            />
          ))}
        </div>

        {/* Windows */}
        {windows.map((window) => {
          const config = WINDOW_CONFIGS[window.id];
          if (!config) return null;

          return (
            <Window
              key={window.instanceId}
              id={window.id}
              title={config.title}
              onClose={closeWindow}
              onFocus={focusWindow}
              isActive={window.zIndex === Math.max(...windows.map(w => w.zIndex))}
              initialPosition={window.position}
              width={config.width}
              height={config.height}
              zIndex={window.zIndex}
            >
              {renderWindowContent(window.id)}
            </Window>
          );
        })}

        {/* TUX Watermark */}
        <div className="absolute bottom-4 right-4 text-primary/10 text-6xl select-none pointer-events-none">
          🐧
        </div>
      </div>

      {/* CRT Scanlines Effect */}
      <div 
        className="fixed inset-0 pointer-events-none z-50"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 102, 0.015) 2px, rgba(0, 255, 102, 0.015) 4px)',
        }}
      />
    </div>
  );
};
